import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Plus, Edit, Trash2, Users, Upload, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getFileUrl } from '../../utils/assetUrlHelper';
import StatusBadge from '../../components/admin/StatusBadge';
import WorkflowActions from '../../components/admin/WorkflowActions';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STAFF_TYPES = [
  { value: 'director', label: 'Director' },
  { value: 'scientific', label: 'Scientific Staff' },
  { value: 'technical', label: 'Technical Staff' },
  { value: 'skilled_supporting', label: 'Skilled Supporting Staff' },
  { value: 'administrative', label: 'Administrative Staff' },
];

const DIVISIONS = [
  { value: 'none', label: 'None' },
  { value: 'crop_improvement', label: 'Crop Improvement' },
  { value: 'crop_production', label: 'Crop Production' },
  { value: 'crop_protection', label: 'Crop Protection' },
  { value: 'post_harvest_management', label: 'Post Harvest Management' },
];

const SUB_INSTITUTES = [
  { value: 'none', label: 'None' },
  { value: 'rrs_malda', label: 'RRS Malda' },
  { value: 'kvk_malda', label: 'KVK Malda' },
];

export default function StaffManagementPage() {
  const { isCreator, isSuperAdmin } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  const [form, setForm] = useState({
    full_name: '',
    designation: '',
    icar_email: '',
    alternate_email: '',
    phone_number: '',
    mobile_number: '',
    specialization: '',
    joining_date: '',
    msc_from: '',
    phd_from: '',
    description: '',
    short_description: '',
    photo_url: '',
    priority: 1,
    staff_type: 'scientific',
    division: 'none',
    sub_institute: 'none',
  });

  useEffect(() => {
    fetchStaff();
  }, []);

  useEffect(() => {
    setPreviewUrl(form.photo_url);
  }, [form.photo_url]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('cish_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('cish_token');
      const res = await axios.get(`${API_URL}/api/staff?published_only=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStaff(res.data);
    } catch (error) {
      console.error('Error fetching staff:', error);
      toast.error('Failed to fetch staff');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('cish_token');
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await fetch(`${API_URL}/api/files/upload?subfolder=staff`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setForm({ ...form, photo_url: data.file_path });
        toast.success('Image uploaded successfully!');
        setSelectedFile(null);
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setForm({ ...form, photo_url: '' });
    setSelectedFile(null);
    setPreviewUrl('');
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Validate required fields
      if (!form.full_name || !form.designation || !form.staff_type) {
        toast.error('Please fill in all required fields');
        setSaving(false);
        return;
      }

      // Convert 'none' to empty string for division and sub_institute
      const dataToSave = {
        ...form,
        division: form.division === 'none' ? '' : form.division,
        sub_institute: form.sub_institute === 'none' ? '' : form.sub_institute,
      };

      // Set workflow status for creators
      if (isCreator) {
        dataToSave.workflow_status = 'PENDING';
        dataToSave.is_published = false;
        dataToSave.visible_to_public = false;
      }

      const endpoint = editingStaff
        ? `${API_URL}/api/staff/${editingStaff.id}`
        : `${API_URL}/api/staff`;
      const method = editingStaff ? 'put' : 'post';

      // Wait for the save to complete and check response
      await axios[method](endpoint, dataToSave, { headers: getAuthHeaders() });

      // Refresh staff list first
      await fetchStaff();

      // Show appropriate message based on user role
      if (isCreator) {
        toast.success(editingStaff ? 'Updated Successfully! Sent for Re-approval' : 'Created Successfully! Sent For Approval');
      } else {
        toast.success(editingStaff ? 'Staff member updated successfully!' : 'Staff member created successfully!');
      }

      setDialogOpen(false);
      setEditingStaff(null);
      resetForm();

    } catch (error) {
      console.error('Error saving staff:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to save staff member';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this staff member?')) return;

    try {
      await axios.delete(`${API_URL}/api/staff/${id}`, { headers: getAuthHeaders() });
      toast.success('Deleted successfully');
      fetchStaff();
    } catch (error) {
      console.error('Error deleting staff:', error);
      toast.error('Failed to delete staff');
    }
  };

  const handleEdit = (staffMember) => {
    setEditingStaff(staffMember);
    setForm({
      full_name: staffMember.full_name || '',
      designation: staffMember.designation || '',
      icar_email: staffMember.icar_email || '',
      alternate_email: staffMember.alternate_email || '',
      phone_number: staffMember.phone_number || '',
      mobile_number: staffMember.mobile_number || '',
      specialization: staffMember.specialization || '',
      joining_date: staffMember.joining_date || '',
      msc_from: staffMember.msc_from || '',
      phd_from: staffMember.phd_from || '',
      description: staffMember.description || '',
      short_description: staffMember.short_description || '',
      photo_url: staffMember.photo_url || '',
      priority: staffMember.priority !== undefined ? staffMember.priority : 1,
      staff_type: staffMember.staff_type || 'scientific',
      division: staffMember.division || 'none',
      sub_institute: staffMember.sub_institute || 'none',
    });
    setSelectedFile(null);
    setPreviewUrl(staffMember.photo_url || '');
    setDialogOpen(true);
  };

  const resetForm = () => {
    setForm({
      full_name: '',
      designation: '',
      icar_email: '',
      alternate_email: '',
      phone_number: '',
      mobile_number: '',
      specialization: '',
      joining_date: '',
      msc_from: '',
      phd_from: '',
      description: '',
      short_description: '',
      photo_url: '',
      priority: 1,
      staff_type: 'scientific',
      division: 'none',
      sub_institute: 'none',
    });
    setSelectedFile(null);
    setPreviewUrl('');
  };

  const filteredStaff = filterType === 'all'
    ? staff
    : staff.filter(s => s.staff_type === filterType);

  return (
    <AdminLayout title="Staff Management">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Staff Management</h1>
            <p className="text-gray-600 text-sm mt-1">Manage staff members and their information</p>
          </div>
          <Button
            onClick={() => {
              setEditingStaff(null);
              resetForm();
              setDialogOpen(true);
            }}
            className="flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Staff Member
          </Button>
        </div>

        {/* Filter */}
        <div className="flex gap-4 items-center">
          <Label>Filter by Type:</Label>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Staff</SelectItem>
              {STAFF_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-sm text-gray-600">
            Total: {filteredStaff.length}
          </span>
        </div>

        {/* Staff Table */}
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" />
            <p className="text-gray-500 mt-2">Loading staff...</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Designation</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Division/Institute</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Is Published</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredStaff.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                      No staff members found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredStaff.map(s => (
                    <TableRow key={s.id}>
                      <TableCell className="font-medium">
                        {s.full_name || s.name}
                        {s.priority === 0 && (
                          <span className="ml-2 px-2 py-0.5 bg-[#0E6D2B] text-white text-xs rounded">
                            HEAD
                          </span>
                        )}
                      </TableCell>
                      <TableCell>{s.designation}</TableCell>
                      <TableCell className="text-sm">
                        {STAFF_TYPES.find(t => t.value === s.staff_type)?.label}
                      </TableCell>
                      <TableCell className="text-sm">
                        {s.division && DIVISIONS.find(d => d.value === s.division)?.label}
                        {s.sub_institute && SUB_INSTITUTES.find(si => si.value === s.sub_institute)?.label}
                        {!s.division && !s.sub_institute && '-'}
                      </TableCell>
                      <TableCell>
                        {s.priority === 0 ? (
                          <span className="text-[#0E6D2B] font-semibold">Head</span>
                        ) : (
                          <span className="text-gray-600">{s.priority}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{s.icar_email || '-'}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs ${s.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                          {s.is_published ? '✓ Yes' : '✗ No'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={s.workflow_status || (s.is_published ? 'PUBLISHED' : 'PENDING')} size="xs" />
                        {s.workflow_status === 'REJECTED' && s.rejection_remark && (
                          <p className="text-xs text-red-600 mt-1">Rejected</p>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-2 justify-end">
                          {s.workflow_status === 'REJECTED' && (
                            <WorkflowActions
                              contentId={s.id}
                              contentType="staff"
                              status={s.workflow_status}
                              token={localStorage.getItem('cish_token')}
                              onStatusChange={() => fetchStaff()}
                              rejectionRemark={s.rejection_remark}
                              rejectedBy={s.rejected_by_name}
                              rejectedAt={s.rejected_at}
                            />
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(s)}
                            disabled={
                              isCreator &&
                              (s.workflow_status === 'PENDING' ||
                                s.workflow_status === 'PUBLISHED' ||
                                (s.is_published && !s.workflow_status))
                            }
                            className="disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          {isSuperAdmin && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(s.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingStaff ? 'Edit Staff Member' : 'Add Staff Member'}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Full Name *</Label>
                  <Input
                    required
                    value={form.full_name}
                    onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  />
                </div>

                <div>
                  <Label>Designation *</Label>
                  <Input
                    required
                    value={form.designation}
                    onChange={(e) => setForm({ ...form, designation: e.target.value })}
                  />
                </div>

                <div>
                  <Label>Staff Type *</Label>
                  <Select value={form.staff_type} onValueChange={(val) => setForm({ ...form, staff_type: val })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STAFF_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="md:col-span-2">
                  <div className="flex items-center gap-2 mb-3">
                    <input
                      type="checkbox"
                      id="mark-as-head"
                      checked={form.priority === 0}
                      onChange={(e) => setForm({ ...form, priority: e.target.checked ? 0 : 1 })}
                      className="w-4 h-4 text-[#0E6D2B] border-gray-300 rounded focus:ring-[#0E6D2B]"
                    />
                    <Label htmlFor="mark-as-head" className="cursor-pointer font-semibold text-[#0E6D2B]">
                      Mark as Head (Priority 0 - Shows First)
                    </Label>
                  </div>

                  {form.priority !== 0 && (
                    <div>
                      <Label>Priority Order (1, 2, 3...)</Label>
                      <Input
                        type="number"
                        min="1"
                        value={form.priority}
                        onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) || 1 })}
                        placeholder="1 = First after head, 2 = Second..."
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Lower numbers appear first (1 appears before 2, 2 before 3, etc.)
                      </p>
                    </div>
                  )}
                </div>

                {form.staff_type === 'scientific' && (
                  <div>
                    <Label>Division</Label>
                    <Select value={form.division} onValueChange={(val) => setForm({ ...form, division: val })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DIVISIONS.map(div => (
                          <SelectItem key={div.value} value={div.value}>
                            {div.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label>Sub-Institute</Label>
                  <Select value={form.sub_institute} onValueChange={(val) => setForm({ ...form, sub_institute: val })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SUB_INSTITUTES.map(si => (
                        <SelectItem key={si.value} value={si.value}>
                          {si.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>ICAR Email</Label>
                  <Input
                    type="email"
                    value={form.icar_email}
                    onChange={(e) => setForm({ ...form, icar_email: e.target.value })}
                  />
                </div>

                <div>
                  <Label>Alternate Email</Label>
                  <Input
                    type="email"
                    value={form.alternate_email}
                    onChange={(e) => setForm({ ...form, alternate_email: e.target.value })}
                  />
                </div>

                <div>
                  <Label>Phone Number</Label>
                  <Input
                    value={form.phone_number}
                    onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
                  />
                </div>

                <div>
                  <Label>Mobile Number</Label>
                  <Input
                    value={form.mobile_number}
                    onChange={(e) => setForm({ ...form, mobile_number: e.target.value })}
                  />
                </div>

                <div>
                  <Label>Joining Date</Label>
                  <Input
                    type="date"
                    value={form.joining_date}
                    onChange={(e) => setForm({ ...form, joining_date: e.target.value })}
                  />
                </div>

                <div>
                  <Label>Specialization</Label>
                  <Input
                    value={form.specialization}
                    onChange={(e) => setForm({ ...form, specialization: e.target.value })}
                  />
                </div>

                <div>
                  <Label>M.Sc. From</Label>
                  <Input
                    value={form.msc_from}
                    onChange={(e) => setForm({ ...form, msc_from: e.target.value })}
                  />
                </div>

                <div>
                  <Label>Ph.D. From</Label>
                  <Input
                    value={form.phd_from}
                    onChange={(e) => setForm({ ...form, phd_from: e.target.value })}
                  />
                </div>

                <div className="md:col-span-2">
                  <Label>Profile Photo</Label>

                  {previewUrl && (
                    <div className="mb-3 relative inline-block">
                      <img
                        src={getFileUrl(previewUrl)}
                        alt="Profile Preview"
                        className="w-32 h-32 object-cover rounded-full border-2 border-gray-200"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}

                  {/* Upload Option */}
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Upload Photo</label>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={handleFileSelect}
                          className="cursor-pointer"
                        />
                      </div>
                      {selectedFile && (
                        <Button
                          type="button"
                          onClick={handleUpload}
                          disabled={uploading}
                          variant="outline"
                          className="flex items-center gap-2"
                        >
                          {uploading ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="w-4 h-4" />
                              Upload
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Recommended: 400x400px, JPG/PNG • Max size: 2MB
                    </p>
                  </div>

                  {/* URL Option */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Or Photo URL <span className="text-gray-400 text-xs">(paste external image link)</span>
                    </label>
                    <Input
                      type="text"
                      value={form.photo_url || ''}
                      onChange={(e) => setForm({ ...form, photo_url: e.target.value })}
                      placeholder="https://example.com/staff/photo.jpg"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      💡 Upload for better performance, or use external URL
                    </p>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <Label>Short Description (150 chars)</Label>
                  <Textarea
                    rows={2}
                    maxLength={300}
                    value={form.short_description}
                    onChange={(e) => setForm({ ...form, short_description: e.target.value })}
                  />
                </div>

                <div className="md:col-span-2">
                  <Label>Full Description/Bio</Label>
                  <Textarea
                    rows={6}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <Button type="submit" disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {editingStaff ? 'Update' : 'Create'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
